import {
  Component,
  OnChanges,
  computed,
  input,
  output,
  ViewChild,
  ElementRef,
  AfterViewInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Injector,
  OnInit,
  afterNextRender,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Modal } from 'flowbite';
import { getRouteForm, getFareValues } from 'src/app/forms/routeForm';
import { IFareData } from 'src/app/models';
import {
  IBase,
  IBasicTemporalPass,
  IFare,
  IPromotionalCode,
  IRouteDetails,
  ITemporalPass,
  ITown,
} from 'src/app/models';
import {
  ExpeditionType,
  FareType,
  ModalQueryParam,
  NotificationTypes,
  UserRole,
  isDoor2DoorExpeditionType,
} from 'src/app/models/enums';
import { ModalFareComponent } from 'src/app/shared/modals/modal-fare/modal-fare.component';
import {
  AppsService,
  FaresService,
  LiteralService,
  ModalUrlService,
  PromotionalCodesService,
  RouteService,
  SaeIntegrations,
  ServiceWizardService,
  StorageService,
  TemporalPassesService,
  UsersService,
  UtilsService,
} from 'src/app/services';
import { images } from 'src/images';

@Component({
  selector: 'app-service-routes-details',
  templateUrl: './service-routes-details.component.html',
  styleUrl: './service-routes-details.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ServiceRoutesDetailsComponent implements OnInit, OnChanges, AfterViewInit {
  route: any = input<IRouteDetails>();
  newRouteObject: any = input<IRouteDetails>();
  readonly newRoute: any = input<boolean>();
  edit: any = input<boolean>();
  town: any = input<ITown>();
  managerClientName: any = input<string>();
  readonly managerClientId = input<number | undefined>();
  readonly closed = input<boolean>();
  readonly designEditMode = input<boolean>(false);
  readonly editEvent = output();
  readonly editEventEmitter = output<IRouteDetails>();
  readonly createEventEmitter = output<IRouteDetails>();
  readonly deleteEventEmitter = output<number>();
  readonly duplicateEventEmitter = output<IRouteDetails>();
  readonly showChangeStatusEventEmitter = output<any>();
  readonly closeDetails = output<boolean | undefined>();
  readonly expandedChange = output<number>();
  readonly fixedTypeSelectedEventEmitter = output<void>();
  readonly showItineraries = computed(() => !isDoor2DoorExpeditionType(this.route()?.expeditionType));

  public editStatus = false;
  public routeModified = false;

  public images = images;
  public routeForm: any[];
  public expanded = false;
  public routeStatusList = [
    { id: 'activated', name: 'ACTIVATED' },
    { id: 'deactivated', name: 'DEACTIVATED' },
  ];

  public saes: any[] = [];

  // Fare modal
  @ViewChild(ModalFareComponent) fareModalComponent: ModalFareComponent;
  public fareModal: Modal;
  public fares: IFare[] = [];
  public farePagination = { page: 0, size: 20 };
  public selectedFare: IFare | undefined;
  public userRole: UserRole;
  public promotionalCodes: IPromotionalCode[] = [];
  public selectedPromotionalCode: IPromotionalCode | undefined;
  public temporalPasses: IBasicTemporalPass[] = [];
  public selectedTemporalPass: IBasicTemporalPass | undefined;
  private fareModalRouteId: number | undefined;
  private pendingFareModalRestore: { routeId: number; tabQueryValue: string } | undefined;

  constructor(
    public literalService: LiteralService,
    private usersService: UsersService,
    public utilsService: UtilsService,
    private routeService: RouteService,
    private saeIntegrations: SaeIntegrations,
    private faresService: FaresService,
    private promotionalCodesService: PromotionalCodesService,
    private temporalPassesService: TemporalPassesService,
    private storageService: StorageService,
    private appsService: AppsService,
    public serviceWizardService: ServiceWizardService,
    private cdr: ChangeDetectorRef,
    private modalUrlService: ModalUrlService,
    private activatedRoute: ActivatedRoute,
    private injector: Injector,
  ) {}

  ngOnInit() {
    this.consumeFareModalUrlState();
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  async ngOnChanges() {
    this.closeFareModalOfAnotherRoute();
    this.routeModified = false;
    if (this.newRoute()) this.expanded = false;
    // A failing request must not stop the refresh below: the panel would stay frozen on
    // whatever it was showing and stop reacting to edit, expand or close
    try {
      const resSaes: IBase[] = await this.saeIntegrations.getSaes();
      this.saes = [
        { id: null, value: '', label: '-' },
        ...resSaes.map((sae: IBase) => ({
          id: sae.id,
          value: sae.name,
          label: sae.name,
        })),
      ];
      this.userRole = await this.storageService.getRole();
      if (this.route() || this.newRoute()) {
        await this.updateRouteForm();
      }
    } catch (error) {
      console.error('Error building the route details form:', error);
    } finally {
      // ngOnChanges resumes outside change detection after the awaits above, so the rebuilt
      // form only reaches the template if it is refreshed here
      this.cdr.detectChanges();
    }
    this.restoreFareModal();
  }

  private async consumeFareModalUrlState() {
    const tabQueryValue = await this.modalUrlService.consumeOpenValue(ModalQueryParam.FARES);
    const routeIdInUrl = Number(this.activatedRoute.snapshot.paramMap.get('routeId'));
    if (tabQueryValue === null || !Number.isInteger(routeIdInUrl) || routeIdInUrl <= 0) return;
    this.pendingFareModalRestore = { routeId: routeIdInUrl, tabQueryValue };
    this.restoreFareModal();
  }

  private restoreFareModal() {
    const pendingRestore = this.pendingFareModalRestore;
    const routeId = this.route()?.id;
    if (!pendingRestore || (!routeId && !this.newRoute())) return;
    this.pendingFareModalRestore = undefined;
    if (this.newRoute() || this.closed() || routeId !== pendingRestore.routeId) return;
    afterNextRender(() => this.openFareModal(pendingRestore.tabQueryValue), { injector: this.injector });
  }

  private closeFareModalOfAnotherRoute() {
    if (this.fareModal?.isVisible() && this.route()?.id !== this.fareModalRouteId) this.fareModal.hide();
  }

  public async updateRouteForm(fareData?: IFareData) {
    await getRouteForm(
      this.newRoute() ? this.newRouteObject() : this.route(),
      this.newRoute(),
      this.usersService,
      this.literalService,
      this.storageService,
      this.appsService,
      this.utilsService,
      this.routeService,
      this.saeIntegrations,
      this.faresService,
      this.town(),
      this.managerClientName(),
      this.edit(),
      fareData,
    ).then((res: any) => {
      this.routeForm = res;
    });
  }

  private emitWidthAfterTransition() {
    const element = document.getElementById('routeDetailsContainer');
    if (!element) return;

    let emitted = false;
    const emitWidth = () => {
      if (emitted) return;
      emitted = true;
      this.expandedChange.emit(Math.max(element.clientWidth, 32));
    };

    const handler = (event: TransitionEvent) => {
      if (event.propertyName === 'width') {
        element.removeEventListener('transitionend', handler);
        emitWidth();
      }
    };
    element.addEventListener('transitionend', handler);

    setTimeout(() => {
      element.removeEventListener('transitionend', handler);
      emitWidth();
    }, 1000);
  }

  onExpand() {
    if (this.closed()) {
      this.closeDetails.emit(false);
    } else {
      this.expanded = !this.expanded;
    }
    this.emitWidthAfterTransition();
  }

  async scrollFares(event: any) {
    if (event.way === 'down') {
      const fareData: IFareData =
        event.search !== ''
          ? { showMore: true, search: true, value: event.search }
          : { showMore: true };
      await this.updateFareField(fareData);
    }
  }

  async searchFares(event: any) {
    await this.updateFareField({
      showMore: false,
      search: true,
      value: event.search,
    });
  }

  private async updateFareField(fareData: IFareData) {
    const targetObject = this.newRoute() ? this.newRouteObject() : this.route();
    const clientId = this.fareClientId;
    if (!clientId) return;
    const fares = await getFareValues(
      this.faresService,
      clientId,
      targetObject,
      fareData,
    );
    const fareElement = this.routeForm?.find(
      (el: any) => el.edit?.valueToChange === 'fare',
    );
    if (fareElement) {
      fareElement.edit.values = fares;
    }
  }

  close() {
    if (this.newRoute() && !this.designEditMode()) {
      this.routeForm = [];
      this.editStatus = false;
      this.closeDetails.emit(undefined);
    } else {
      if (this.expanded) this.expanded = false;
      this.emitWidthAfterTransition();
      this.closeDetails.emit(true);
    }
  }

  async downloadGTFS() {
    const response = await this.routeService.downloadGtfs(this.route().id);
    this.utilsService.downloadDocument(response as any, 'gtfs', 'zip');
  }

  // Keystroke-level update so the wizard Continue button reacts without leaving the field;
  // the full changeData still runs on change with its per-field logic and form rebuild
  liveChangeData(data: any, element?: any) {
    const valueToChange = element?.edit?.valueToChange;
    const excludedFields = [
      'publishingSites',
      'additionalClients',
      'apps',
      'saeIntegration',
      'expeditionType',
      'ticketingType',
      'fare',
    ];
    if (
      !valueToChange ||
      Array.isArray(valueToChange) ||
      excludedFields.includes(valueToChange)
    )
      return;
    const targetObject = this.newRoute() ? this.newRouteObject() : this.route();
    const value =
      typeof data === 'object' ? (data.target?.value ?? data) : data;
    if (targetObject) targetObject[valueToChange] = value;
  }

  changeData(data: any, element?: any) {
    const targetObject = this.newRoute() ? this.newRouteObject() : this.route();
    this.routeModified = true;
    if (
      element.edit?.valueToChange === 'publishingSites' ||
      element.edit?.valueToChange === 'additionalClients' ||
      element.edit?.valueToChange === 'apps'
    ) {
      return;
    }
    if (element.edit?.valueToChange === 'saeIntegration') {
      const value =
        typeof data === 'object' ? data.target || data.options[0] : data;
      const selectedSae = this.saes.find(
        (sae: any) =>
          sae.value === this.setNormalData(value, typeof data === 'object'),
      );
      targetObject[element.edit?.valueToChange] = selectedSae?.id
        ? { id: selectedSae.id, name: selectedSae.value }
        : null;
      this.updateRouteForm();
      return;
    } else if (element.edit?.valueToChange === 'expeditionType') {
      const value =
        typeof data === 'object' ? data.target || data.options[0] : data;
      targetObject[element.edit?.valueToChange] = this.setNormalData(
        value,
        typeof data === 'object',
      );
      if (
        this.newRoute() &&
        targetObject[element.edit?.valueToChange] === ExpeditionType.Fixed
      ) {
        // Fixed routes define their ordered stops through the stops modal before the rest of the form
        this.fixedTypeSelectedEventEmitter.emit();
      }
      this.updateRouteForm();
      return;
    } else if (element.edit?.valueToChange === 'ticketingType') {
      const value =
        typeof data === 'object' ? data.target || data.options[0] : data;
      const ticketingValue = this.setNormalData(
        value,
        typeof data === 'object',
      );
      targetObject[element.edit?.valueToChange] = ticketingValue;
      if (!ticketingValue || ticketingValue === 'None') {
        targetObject.fare = null;
      }
      this.updateRouteForm();
      return;
    } else if (element.edit?.valueToChange === 'fare') {
      const value =
        typeof data === 'object' ? data.target || data.options?.[0] : data;
      const selectedName = this.setNormalData(value, typeof data === 'object');
      const selectedFare = element.edit?.values?.find(
        (fare: any) => fare.name === selectedName,
      );
      if (selectedFare?.id) {
        const { value: _, label: __, ...fareWithoutSelectProps } = selectedFare;
        targetObject[element.edit?.valueToChange] = fareWithoutSelectProps;
      } else {
        targetObject[element.edit?.valueToChange] = null;
      }
      this.updateRouteForm();
      return;
    } else if (Array.isArray(element.edit?.valueToChange)) {
      this.setMultiSelectData(element, targetObject);
    } else {
      const value =
        typeof data === 'object' ? data.target || data.options[0] : data;
      targetObject[element.edit?.valueToChange] = this.setNormalData(
        value,
        typeof data === 'object',
      );
    }
  }

  private setNormalData(data: any, isEvent = false) {
    return isEvent ? data.value : data;
  }

  private setMultiSelectData(element: any, targetObject: any) {
    const isSaeGroup = element.edit?.valueToChange[0] === 'saeActions';
    const isPublishingSitesGroup =
      element.edit?.valueToChange[0] === 'publishingSites';
    const valueToChange =
      isSaeGroup || isPublishingSitesGroup
        ? element.edit?.valueToChange[1]
        : element.edit?.valueToChange;
    valueToChange.map((value: string) => {
      let checked = false;
      if (
        element.edit?.selectedValues.some(
          (selectedValue: any) => selectedValue.id === value,
        )
      ) {
        checked = true;
      }
      if (isSaeGroup || isPublishingSitesGroup) {
        const type = isSaeGroup ? 'saeActions' : 'publishingSites';
        const group = targetObject[type] as string[];
        const index = targetObject[type].indexOf(value);
        checked
          ? index === -1 && group.push(value)
          : index > -1 && group.splice(index, 1);
      } else {
        targetObject[value] = checked;
      }
    });
  }

  editStatusClick() {
    this.editStatus = !this.editStatus;
  }

  changeStatus(data: any) {
    this.editStatus = false;
    this.showChangeStatusEventEmitter.emit({
      id: this.route().id,
      value: data.value,
    });
  }

  onSubmit() {
    if (this.newRoute()) {
      this.createEventEmitter.emit(this.newRouteObject());
    } else {
      this.editEventEmitter.emit(this.route());
    }
  }

  isFormValid(): boolean {
    if (!this.routeForm) return false;
    const targetObject = this.newRoute() ? this.newRouteObject() : this.route();
    return this.routeForm.every((element: any) => {
      if (!element.required && !element.edit?.required) return true;
      const valueToChange = element.edit?.valueToChange;
      if (!valueToChange) return true;
      const value = targetObject[valueToChange];
      return (
        value !== null && value !== undefined && value !== '' && value !== '-'
      );
    });
  }

  // Fares are managed right away, also from a route not created yet: they are not part of its creation
  get fareClientId(): number | undefined {
    const targetObject = this.newRoute() ? this.newRouteObject() : this.route();
    return targetObject?.managerClient?.id ?? this.managerClientId();
  }

  get fareRouteId(): number | undefined {
    return this.newRoute() ? undefined : this.route()?.id;
  }

  async openFareModal(tabQueryValue = '') {
    const clientId = this.fareClientId;
    if (clientId) {
      this.fares = await this.faresService.getFares(
        clientId,
        undefined,
        undefined,
        undefined,
        true,
      );
    } else {
      this.fares = [];
    }
    this.selectedFare = this.route()?.fare;
    await this.loadPromotionalCodes();
    await this.loadTemporalPasses();
    this.fareModal = new Modal(this.fareModalComponent.modalContainer().nativeElement, {
      closable: false,
      onHide: () => {
        this.farePagination = { page: 0, size: 20 };
        this.fareModalComponent?.resetState();
        this.modalUrlService.markClosed(ModalQueryParam.FARES);
      },
    });
    this.fareModalRouteId = this.route()?.id;
    this.fareModal.show();
    this.fareModalComponent.showTabFromQueryValue(tabQueryValue);
  }

  private async loadPromotionalCodes() {
    const clientId = this.fareClientId;
    if (!clientId) {
      this.promotionalCodes = [];
      return;
    }
    try {
      const all = await this.promotionalCodesService.getPromotionalCodes();
      this.promotionalCodes = all.filter((c) => c.client?.id === clientId);
    } catch (error) {
      console.error('Error loading promotional codes:', error);
      this.promotionalCodes = [];
    }
  }

  private async loadTemporalPasses() {
    const clientId = this.fareClientId;
    if (!clientId) {
      this.temporalPasses = [];
      return;
    }
    try {
      const all = await this.temporalPassesService.getTemporalPasses();
      this.temporalPasses = all.filter((p) => p.client?.id === clientId);
    } catch (error) {
      console.error('Error loading temporal passes:', error);
      this.temporalPasses = [];
    }
  }

  async handlePromotionalCodeAction(event: any) {
    const promo = event.promotionalCode as IPromotionalCode & {
      clientId: number;
    };
    if (event.isCreating) {
      try {
        const created =
          await this.promotionalCodesService.createPromotionalCode({
            code: promo.code,
            clientId: promo.clientId,
            discountType: promo.discountType,
            discountValue: promo.discountValue,
            validFrom: promo.validFrom,
            validUntil: promo.validUntil,
            maxUsages: promo.maxUsages,
          });
        await this.loadPromotionalCodes();
        this.utilsService.showNotification(
          this.images.notifications.check,
          'services.routes.promotionalCode',
          NotificationTypes.SUCCESS,
          'createSuccess.title',
          'createSuccess.text',
        );
        await this.fareModalComponent?.onPromotionalCodeCreateSuccess(created);
      } catch (error) {
        console.error('Error creating promotional code:', error);
        this.utilsService.showNotification(
          this.images.notifications.warning,
          'services.routes.promotionalCode',
          NotificationTypes.DANGER,
          'createError.title',
          'createError.text',
        );
      }
    } else if (event.isEditing && promo.id) {
      try {
        const updated =
          await this.promotionalCodesService.updatePromotionalCode(promo.id, {
            code: promo.code,
            clientId: promo.clientId,
            discountType: promo.discountType,
            discountValue: promo.discountValue,
            validFrom: promo.validFrom,
            validUntil: promo.validUntil,
            maxUsages: promo.maxUsages,
          });
        await this.loadPromotionalCodes();
        this.utilsService.showNotification(
          this.images.notifications.check,
          'services.routes.promotionalCode',
          NotificationTypes.SUCCESS,
          'updateSuccess.title',
          'updateSuccess.text',
        );
        await this.fareModalComponent?.onPromotionalCodeEditSuccess(updated);
      } catch (error) {
        console.error('Error updating promotional code:', error);
        this.utilsService.showNotification(
          this.images.notifications.warning,
          'services.routes.promotionalCode',
          NotificationTypes.DANGER,
          'updateError.title',
          'updateError.text',
        );
      }
    }
  }

  handleDeletePromotionalCode(code: IPromotionalCode) {
    return this.runDeletePromotionalCode(code);
  }

  async handleActivatePromotionalCode(code: IPromotionalCode) {
    if (!code.id) return;
    try {
      await this.promotionalCodesService.activatePromotionalCode(code.id);
      await this.loadPromotionalCodes();
      const refreshed = this.promotionalCodes.find((c) => c.id === code.id);
      if (refreshed)
        await this.fareModalComponent?.onPromotionalCodeEditSuccess(refreshed);
      this.utilsService.showNotification(
        this.images.notifications.check,
        'services.routes.promotionalCode',
        NotificationTypes.SUCCESS,
        'activateSuccess.title',
        'activateSuccess.text',
      );
    } catch (error) {
      console.error('Error activating promotional code:', error);
      this.utilsService.showNotification(
        this.images.notifications.warning,
        'services.routes.promotionalCode',
        NotificationTypes.DANGER,
        'activateError.title',
        'activateError.text',
      );
    }
  }

  private async runDeletePromotionalCode(code: IPromotionalCode) {
    if (!code.id) return;
    try {
      await this.promotionalCodesService.deactivatePromotionalCode(code.id);
      await this.loadPromotionalCodes();
      const refreshed = this.promotionalCodes.find((c) => c.id === code.id);
      if (refreshed) {
        await this.fareModalComponent?.onPromotionalCodeEditSuccess(refreshed);
      } else {
        this.fareModalComponent?.onPromotionalCodeDeleteSuccess();
      }
      this.utilsService.showNotification(
        this.images.notifications.check,
        'services.routes.promotionalCode',
        NotificationTypes.SUCCESS,
        'deactivateSuccess.title',
        'deactivateSuccess.text',
      );
    } catch (error) {
      console.error('Error deactivating promotional code:', error);
      this.utilsService.showNotification(
        this.images.notifications.warning,
        'services.routes.promotionalCode',
        NotificationTypes.DANGER,
        'deactivateError.title',
        'deactivateError.text',
      );
    }
  }

  async handleTemporalPassAction(event: any) {
    const pass = event.temporalPass as ITemporalPass & {
      clientId: number;
      fareIds: number[];
    };
    if (event.isCreating) {
      try {
        const created = await this.temporalPassesService.createTemporalPass({
          name: pass.name,
          price: pass.price,
          clientId: pass.clientId,
          fareIds: pass.fareIds,
          roundToNaturalPeriod: pass.roundToNaturalPeriod,
          type: pass.type,
          usageLimit: pass.usageLimit,
        });
        await this.loadTemporalPasses();
        this.utilsService.showNotification(
          this.images.notifications.check,
          'services.routes.temporalPass',
          NotificationTypes.SUCCESS,
          'createSuccess.title',
          'createSuccess.text',
        );
        await this.fareModalComponent?.onTemporalPassCreateSuccess(created);
      } catch (error) {
        console.error('Error creating temporal pass:', error);
        this.utilsService.showNotification(
          this.images.notifications.warning,
          'services.routes.temporalPass',
          NotificationTypes.DANGER,
          'createError.title',
          'createError.text',
        );
      }
    } else if (event.isEditing && pass.id) {
      try {
        const updated = await this.temporalPassesService.updateTemporalPass(
          pass.id,
          {
            name: pass.name,
            price: pass.price,
            clientId: pass.clientId,
            fareIds: pass.fareIds,
            roundToNaturalPeriod: pass.roundToNaturalPeriod,
            type: pass.type,
            usageLimit: pass.usageLimit,
          },
        );
        await this.loadTemporalPasses();
        this.utilsService.showNotification(
          this.images.notifications.check,
          'services.routes.temporalPass',
          NotificationTypes.SUCCESS,
          'updateSuccess.title',
          'updateSuccess.text',
        );
        await this.fareModalComponent?.onTemporalPassEditSuccess(updated);
      } catch (error) {
        console.error('Error updating temporal pass:', error);
        this.utilsService.showNotification(
          this.images.notifications.warning,
          'services.routes.temporalPass',
          NotificationTypes.DANGER,
          'updateError.title',
          'updateError.text',
        );
      }
    }
  }

  handleDeleteTemporalPass(pass: IBasicTemporalPass) {
    return this.runDeleteTemporalPass(pass);
  }

  async handleActivateTemporalPass(pass: IBasicTemporalPass) {
    if (!pass.id) return;
    try {
      await this.temporalPassesService.activateTemporalPass(pass.id);
      await this.loadTemporalPasses();
      const refreshed = await this.temporalPassesService.getTemporalPass(
        pass.id,
      );
      if (refreshed)
        await this.fareModalComponent?.onTemporalPassEditSuccess(refreshed);
      this.utilsService.showNotification(
        this.images.notifications.check,
        'services.routes.temporalPass',
        NotificationTypes.SUCCESS,
        'activateSuccess.title',
        'activateSuccess.text',
      );
    } catch (error) {
      console.error('Error activating temporal pass:', error);
      this.utilsService.showNotification(
        this.images.notifications.warning,
        'services.routes.temporalPass',
        NotificationTypes.DANGER,
        'activateError.title',
        'activateError.text',
      );
    }
  }

  private async runDeleteTemporalPass(pass: IBasicTemporalPass) {
    if (!pass.id) return;
    try {
      await this.temporalPassesService.deactivateTemporalPass(pass.id);
      await this.loadTemporalPasses();
      try {
        const refreshed = await this.temporalPassesService.getTemporalPass(
          pass.id,
        );
        await this.fareModalComponent?.onTemporalPassEditSuccess(refreshed);
      } catch {
        this.fareModalComponent?.onTemporalPassDeleteSuccess();
      }
      this.utilsService.showNotification(
        this.images.notifications.check,
        'services.routes.temporalPass',
        NotificationTypes.SUCCESS,
        'deactivateSuccess.title',
        'deactivateSuccess.text',
      );
    } catch (error) {
      console.error('Error deactivating temporal pass:', error);
      this.utilsService.showNotification(
        this.images.notifications.warning,
        'services.routes.temporalPass',
        NotificationTypes.DANGER,
        'deactivateError.title',
        'deactivateError.text',
      );
    }
  }

  async handleFareAction(event: any) {
    const clientId = this.fareClientId;
    if (event.isSelecting) {
      // Just selecting an existing fare for the route
      const targetObject = this.newRoute()
        ? this.newRouteObject()
        : this.route();
      targetObject.fare = event.fare;
      this.updateRouteForm();
    } else if (event.isCreating) {
      // Creating a new fare
      const fareRequest = this.buildFareRequest(event.fare);
      try {
        const createdFare = await this.faresService.createFare(
          fareRequest,
          event.fare.type,
        );
        if (clientId) {
          this.fares = await this.faresService.getFares(
            clientId,
            undefined,
            undefined,
            undefined,
            true,
          );
        }
        this.utilsService.showNotification(
          this.images.notifications.check,
          'services.routes.fare',
          NotificationTypes.SUCCESS,
          'createSuccess.title',
          'createSuccess.text',
        );
        await this.fareModalComponent?.onCreateSuccess(createdFare);
      } catch (error) {
        console.error('Error creating fare:', error);
        this.utilsService.showNotification(
          this.images.notifications.warning,
          'services.routes.fare',
          NotificationTypes.DANGER,
          'createError.title',
          'createError.text',
        );
      }
    } else if (event.isEditing) {
      // Updating an existing fare
      const fareRequest = this.buildFareRequest(event.fare);
      const fareId = event.fare.id;
      const fareType: FareType | undefined = event.fare.type;
      if (!fareId) {
        console.error('Invalid fare ID:', event.fare.id);
        return;
      }
      if (!fareType) {
        console.error('Invalid fare type:', event.fare.type);
        return;
      }
      try {
        await this.faresService.updateFare(fareId, fareRequest, fareType);
        if (clientId) {
          this.fares = await this.faresService.getFares(
            clientId,
            undefined,
            undefined,
            undefined,
            true,
          );
        }
        this.utilsService.showNotification(
          this.images.notifications.check,
          'services.routes.fare',
          NotificationTypes.SUCCESS,
          'updateSuccess.title',
          'updateSuccess.text',
        );
        await this.fareModalComponent?.onEditSuccess(fareId, fareType);
      } catch (error) {
        console.error('Error updating fare:', error);
        this.utilsService.showNotification(
          this.images.notifications.warning,
          'services.routes.fare',
          NotificationTypes.DANGER,
          'updateError.title',
          'updateError.text',
        );
      }
    }
  }

  handleDeleteFare(fare: IFare) {
    return this.runDeleteFare(fare);
  }

  async handleActivateFare(fare: IFare) {
    if (!fare.id) return;
    const clientId = this.fareClientId;
    try {
      await this.faresService.activateFare(fare.id);
      if (clientId) {
        this.fares = await this.faresService.getFares(
          clientId,
          undefined,
          undefined,
          undefined,
          true,
        );
      }
      if (fare.id && fare.type) {
        await this.fareModalComponent?.onEditSuccess(fare.id, fare.type);
      }
      this.utilsService.showNotification(
        this.images.notifications.check,
        'services.routes.fare',
        NotificationTypes.SUCCESS,
        'activateSuccess.title',
        'activateSuccess.text',
      );
    } catch (error) {
      console.error('Error activating fare:', error);
      this.utilsService.showNotification(
        this.images.notifications.warning,
        'services.routes.fare',
        NotificationTypes.DANGER,
        'activateError.title',
        'activateError.text',
      );
    }
  }

  private async runDeleteFare(fare: IFare) {
    if (!fare.id) return;
    const clientId = this.fareClientId;
    try {
      await this.faresService.deactivateFare(fare.id);
      if (clientId) {
        this.fares = await this.faresService.getFares(
          clientId,
          undefined,
          undefined,
          undefined,
          true,
        );
      }
      const targetObject = this.newRoute()
        ? this.newRouteObject()
        : this.route();
      if (targetObject.fare?.id === fare.id) {
        targetObject.fare = undefined;
      }
      this.updateRouteForm();
      if (fare.id && fare.type) {
        await this.fareModalComponent?.onEditSuccess(fare.id, fare.type);
      } else {
        this.fareModalComponent?.onDeleteSuccess();
      }
      this.utilsService.showNotification(
        this.images.notifications.check,
        'services.routes.fare',
        NotificationTypes.SUCCESS,
        'deactivateSuccess.title',
        'deactivateSuccess.text',
      );
    } catch (error) {
      console.error('Error deactivating fare:', error);
      this.utilsService.showNotification(
        this.images.notifications.warning,
        'services.routes.fare',
        NotificationTypes.DANGER,
        'deactivateError.title',
        'deactivateError.text',
      );
    }
  }

  async handleLoadMoreFares(event: any) {
    if (event.way === 'down') {
      const clientId = this.fareClientId;
      if (clientId) {
        this.farePagination.page += 1;
        const newFares = await this.faresService.getFares(
          clientId,
          this.farePagination.size,
          this.farePagination.page,
        );
        this.fares = [...this.fares, ...newFares];
      }
    }
  }

  async handleSearchFare(event: any) {
    const clientId = this.fareClientId;
    if (!clientId) return;
    this.farePagination.page = 0;
    this.fares = await this.faresService.getFares(
      clientId,
      this.farePagination.size,
      this.farePagination.page,
      event.search,
    );
  }

  private buildFareRequest(fare: IFare): any {
    const baseRequest = {
      id: fare.id ?? null,
      name: fare.name,
      clientId: this.fareClientId,
      routeId: this.fareRouteId,
      basePrice: fare.basePrice,
      sameDayValidity: fare.sameDayValidity ?? true,
      active: fare.active ?? true,
    };

    switch (fare.type) {
      case FareType.Basic:
        return baseRequest;
      case FareType.Flexible:
        // Flat structure - class extends FareRequest so inherited fields + variablePrice
        return { ...baseRequest, variablePrice: fare.variablePrice };
      case FareType.Ranged_Flexible:
        // Flat structure - class extends FareRequest so inherited fields + ranges
        return { ...baseRequest, ranges: fare.priceRanges };
      default:
        return baseRequest;
    }
  }
}
